/** The multi-buy: in one payment, which is one parcel, the dearest piece is at
 *  its own price and every other piece is a quarter off (the percent is the
 *  server's, seed prices.multiBuy). The page works the bill out itself so the
 *  order sheet can show it line by line and the button can carry the total,
 *  and sends that total with the payment; the server works it out again with
 *  the same function (bill() in functions/src/lib/together.ts) and refuses a
 *  payment whose total is not the one the page showed, rather than charge a
 *  number nobody saw. So this is the server's function, copied as it is, and
 *  check-templates runs both on the same baskets to hold them together.
 *
 *  Plain TypeScript the way Node can load it by stripping the types (no
 *  imports, no enums, no parameter properties): the check reads this very
 *  file, not a build of it. The two `!` in the loop are the only difference
 *  from the reference written in the spec: both tsconfigs check indexed
 *  access, and `!` is erased with the types, so it changes nothing that runs.
 *
 *  What goes in, per asked line in the order the page sends them: `fullCents`
 *  is the list price, VAT inside, before any code, which decides the dearest
 *  piece (a code on one preview of theirs and none on another must not make
 *  the cheaper piece the "first" one and take nothing off); `cents` is the
 *  chip the page shows, after the lead's code; `extraCents` is what a further
 *  piece costs, the larger of the code and the multi-buy taken off the list
 *  price, never both; `qty` is how many of that piece.
 *
 *  The dearest line, when it has more than one piece and something comes off,
 *  is one Stripe line at the whole amount for all its pieces (`bundled`), so
 *  every asked line stays exactly one line on the checkout and in the
 *  server's records. */

export const MAX_UNITS = 10;

export interface BillIn { fullCents: number; cents: number; extraCents: number; qty: number }
export interface BillLine {
  /** Stripe's quantity and unit_amount for this line. */
  quantity: number;
  unitAmount: number;
  /** The real number of pieces on the line (a bundled line is quantity 1). */
  units: number;
  /** Minor units the multi-buy took off this line, against `cents` per unit. */
  off: number;
  amount: number;
  bundled: boolean;
  dearest: boolean;
}
export interface Bill { lines: BillLine[]; total: number; units: number; off: number }

export function bill(lines: BillIn[]): Bill {
  let d = -1;
  for (let i = 0; i < lines.length; i++) if (d < 0 || lines[i]!.fullCents > lines[d]!.fullCents) d = i;
  const out: BillLine[] = lines.map((l, i) => {
    const extra = Math.min(l.extraCents, l.cents);
    if (i === d) {
      const off = (l.qty - 1) * (l.cents - extra);
      if (l.qty === 1 || off === 0) {
        return { quantity: l.qty, unitAmount: l.cents, units: l.qty, off: 0, amount: l.qty * l.cents, bundled: false, dearest: true };
      }
      const amount = l.cents + (l.qty - 1) * extra;
      return { quantity: 1, unitAmount: amount, units: l.qty, off, amount, bundled: true, dearest: true };
    }
    return { quantity: l.qty, unitAmount: extra, units: l.qty, off: l.qty * (l.cents - extra), amount: l.qty * extra, bundled: false, dearest: false };
  });
  return {
    lines: out,
    total: out.reduce((s, l) => s + l.amount, 0),
    units: out.reduce((s, l) => s + l.units, 0),
    off: out.reduce((s, l) => s + l.off, 0),
  };
}

/** An amount in minor units, written the way the server writes every price
 *  on the page: filled into the offer's own `format` ("€{{units}}.{{cents}}",
 *  "{{units}},{{cents}} €"), as money() in functions/src/lib/templates.ts
 *  fills the same one. A sum written any other way would be a total the
 *  server does not recognise, and the payment would be refused for it. */
export function fill(format: string, minor: number): string {
  return format
    .split("{{units}}")
    .join(String(Math.floor(minor / 100)))
    .split("{{cents}}")
    .join(String(minor % 100).padStart(2, "0"));
}
